import {
  PaymentFrequency,
  TaskType,
  CreateTaskInput,
  TaskDirection,
  S3UploadLevel,
  S3UploadInput,
  S3UploadType,
  PaymentType,
  CreateTaskStatus,
  UpdateTaskStatus,
  InvoiceStatus,
  FromToType,
  TaskSettlementStatus,
  DocumentType,
  TaskStatus,
  SignerInput,
} from '@admiin-com/ds-graphql';
import { isAllSigned } from '../../../helpers/signature';
import { useSignedDocumentUpload } from '../../../hooks/useSignedDocumentUpload';
import { transformDocumentAnalysisLineItemInput } from '../../../components/PayBill/useBillCreate/transformer';
import { isEntityOrContact, isNormalContact } from '../../../helpers/entities';
import { getAmountFromLineItems, isTaxEntity } from '../../../helpers/tasks';
import { BillCreateFormData } from './TaskCreationProvider';
import _ from 'lodash';
import { useCreateContact } from '../../../hooks/useCreateContact/useCreateContact';
import { useUpdateContact } from '../../../hooks/useUpdateContact/useUpdateContact';
import { useEntityId } from '../../hooks/useEntityContext/useEntityContext';

export type settlementStatus = 'Payable' | 'Refundable';
export type Signer = {
  id?: string;
  email: string;
  name: string;
  signerType: 'CONTACT' | 'ENTITY_USER' | 'GUEST';
  entityId?: string;
  secondName?: string;
  mobile?: string;
  createContact?: boolean;
  // No Need to send backend
  color?: any;
  noLookup?: boolean;
};
export function useGetTaskInput() {
  const { uploadSignedDocumentFromS3 } = useSignedDocumentUpload();
  const [createContact] = useCreateContact();
  const [updateContact] = useUpdateContact();
  const currentEntityId = useEntityId();

  const createTaskInputValues = async (
    values: BillCreateFormData,
    options: {
      savingAsDraft?: boolean;
      taskType?: 'tax' | 'esignature' | 'invoice' | 'paybill';
      status?: TaskStatus;
    }
  ) => {
    if (options.taskType === 'tax' || options.taskType === 'esignature') {
      values = preESignatureSubmit(values);
    }
    const id = values.id;
    const entityId = values.entityId;

    // Validate that client entity has an owner when sending tasks
    if (
      values.to &&
      isEntityOrContact(values.to) === FromToType.ENTITY &&
      !values.to.owner &&
      (values.type === TaskType.PAY_ONLY ||
        values.type === TaskType.SIGN_PAY ||
        values.type === TaskType.SIGN_ONLY ||
        values.type === TaskType.QUOTE)
    )
      throw new Error('TO_MISSING_OWNER');

    const recipientHasEmail = (recipient: any) => {
      if (!recipient) return false;
      const toType = isEntityOrContact(recipient);
      const email =
        toType === FromToType.CONTACT
          ? recipient.email ?? recipient.contact?.email
          : recipient.contact?.email ??
            recipient.owner?.email ??
            recipient.email;
      return typeof email === 'string' && email.trim() !== '';
    };

    const requiresRecipientEmail =
      values.type === TaskType.PAY_ONLY ||
      values.type === TaskType.SIGN_PAY ||
      values.type === TaskType.SIGN_ONLY ||
      values.type === TaskType.QUOTE;

    // Validate that client/contact has email when sending tasks
    console.log('values.to: ', values.to);
    if (values.to && requiresRecipientEmail && !recipientHasEmail(values.to))
      throw new Error('TO_MISSING_EMAIL');

    const documents: S3UploadInput[] = [];
    let annotationsDocument: any | null = null;
    if (values.annotations) {
      try {
        annotationsDocument = JSON.parse(values.annotations);
      } catch (error) {
        console.error('Failed to parse annotations JSON', error);
      }
    }

    const updateAnnotationsForSigner = (
      signerId: string | undefined,
      updates: Record<string, any>
    ) => {
      if (!signerId || !annotationsDocument?.annotations) return;
      annotationsDocument.annotations = annotationsDocument.annotations.map(
        (annotation: any) => {
          if (annotation?.customData?.signerId === signerId) {
            return {
              ...annotation,
              customData: {
                ...annotation.customData,
                ...updates,
              },
            };
          }
          return annotation;
        }
      );
    };

    if (Array.isArray(values.signers)) {
      for (const signer of values.signers) {
        if (!signer) continue;
        if (signer.signerType === 'GUEST') {
          if (signer.createContact && signer.email) {
            const [firstName, ...rest] = (signer.name ?? signer.email ?? '')
              .trim()
              .split(/\s+/);
            const lastName = rest.join(' ');
            try {
              const createdContact = await createContact({
                variables: {
                  input: {
                    entityId: signer.entityId ?? currentEntityId,
                    firstName: firstName,
                    lastName: lastName,
                    name: signer.name ?? `${firstName} ${lastName}`.trim(),
                    email: signer.email,
                    mobile: signer.mobile ?? '',
                  },
                },
              });
              const contact = createdContact?.data?.createContact;
              if (contact?.id) {
                const previousId = signer.id;
                signer.id = contact.id;
                signer.signerType = 'CONTACT';
                signer.entityId = contact.entityId;
                signer.createContact = false;
                updateAnnotationsForSigner(previousId, {
                  signerId: contact.id,
                  signerType: 'CONTACT',
                  email: contact.email ?? signer.email,
                  mobile: signer.mobile ?? contact.mobile ?? '',
                });
              }
            } catch (error) {
              console.error('Failed to create contact for signer', error);
            }
          } else {
            updateAnnotationsForSigner(signer.id, {
              mobile: signer.mobile ?? '',
            });
          }
        } else if (
          signer.signerType === 'CONTACT' &&
          signer.id &&
          signer.mobile &&
          signer.mobile.trim()
        ) {
          try {
            await updateContact({
              variables: {
                input: {
                  id: signer.id,
                  entityId: signer.entityId ?? currentEntityId,
                  mobile: signer.mobile,
                },
              },
            });
          } catch (error) {
            console.error('Failed to update contact mobile', error);
          }
          updateAnnotationsForSigner(signer.id, {
            mobile: signer.mobile,
          });
        } else if (signer.mobile) {
          updateAnnotationsForSigner(signer.id, {
            mobile: signer.mobile,
          });
        }
      }
    }

    if (annotationsDocument) {
      values.annotations = JSON.stringify(annotationsDocument);
    }
    for (const document of values.documents) {
      if (document) {
        const isSigned = isAllSigned(values.annotations);

        if (!isSigned || options.savingAsDraft || values.id)
          documents.push({
            level: document.level as S3UploadLevel,
            key: document.key,
            identityId: document.identityId,
            type: document.type as S3UploadType,
          });
        else {
          const newKey = await uploadSignedDocumentFromS3(
            {
              key: document.key,
              identityId: document.identityId,
              level: document.level as S3UploadLevel,
              type: document.type as S3UploadType,
            },
            values.annotations
          );
          documents.push({
            level: document.level as S3UploadLevel,
            key: newKey,
            identityId: document.identityId,
            type: document.type as S3UploadType,
          });
        }
      }
    }

    const isTax =
      (values.type === 'PAY_ONLY' && isTaxEntity(values.from)) ||
      (values.type === 'SIGN_PAY' && isTaxEntity(values.futureTaskInput?.from));

    let dueAt: string;
    if (isTax) {
      dueAt = values.paymentAt || values.lodgementAt || values.dueAt;
    } else if (
      values.paymentFrequency !== PaymentFrequency.ONCE &&
      values.firstPaymentAt
    ) {
      dueAt = values.firstPaymentAt;
    } else {
      dueAt = values.dueAt;
    }

    const payableInfo = {
      lineItems: values.lineItems.map((lineItem) => ({
        description: lineItem.description,
        quantity: lineItem.quantity,
        unitAmount: lineItem.unitAmount,
        taxType: lineItem.taxType,
        lineAmount: lineItem.lineAmount,
        serviceId: lineItem.serviceId,
        newService: lineItem.newService,
      })),
      amount: getAmountFromLineItems(values.lineItems).total,
      // tax: Math.round(Number(getAmountFromLineItems(values.lineItems).totalGST / 100) * 100),
      paymentFrequency:
        (values.type === TaskType.SIGN_ONLY
          ? undefined
          : values.paymentFrequency) ?? PaymentFrequency.ONCE,
      isUnlimitedRecurring:
        values.paymentFrequency &&
        values.paymentFrequency !== PaymentFrequency.ONCE &&
        (!values.numberOfPayments || values.numberOfPayments < 2),
      gstInclusive: values.gstInclusive,
      // gstAmount: values.gstInclusive
      //   ? Math.round((Number(values.amount) * 100) / GST_RATE)
      //   : 0,
      paymentTypes: [
        PaymentType.PAY_NOW,
        PaymentType.SCHEDULED,
        ...(isTax || values.allowExtendedTerms
          ? [PaymentType.INSTALLMENTS]
          : []),
      ],
      maximumInstallments: !isTax && values.allowExtendedTerms ? 3 : null,
      paymentAt: values.paymentAt === '' ? undefined : values.paymentAt,
    };

    let otherRecipients = values.otherRecipients;
    if (values.sendCopyToContact && values.from?.email) {
      otherRecipients = [
        ...otherRecipients,
        {
          email: values.from?.email,
          sendOnCompletion: true,
        },
      ];
    }

    const signableInfo = {
      documents:
        documents.length > 0
          ? documents
          : [
              {
                level: S3UploadLevel.private,
                type: S3UploadType.PDF,
                key: 'blank',
                identityId: '',
              },
            ],
      annotations: values.annotations ? values.annotations : null,
      otherRecipients: otherRecipients,
    };
    const taxInfo = {
      lodgementAt: values.lodgementAt === '' ? undefined : values.lodgementAt,
      settlementStatus: isTax ? values.settlementStatus : undefined,
      documentType: values.documentType ?? DocumentType.BAS,
    };
    const payable = values.type !== TaskType.SIGN_ONLY;
    const signable = values.type !== TaskType.PAY_ONLY;
    const task: CreateTaskInput = {
      fromId: values.from?.id,
      fromType: isEntityOrContact(values.from) ?? FromToType.CONTACT,
      toId: values.to?.id,
      toType: isEntityOrContact(values.to),
      type:
        values.type ??
        (options.taskType === 'invoice' || options.taskType === 'paybill'
          ? TaskType.PAY_ONLY
          : TaskType.SIGN_ONLY),
      reference: values.reference,
      bpayReferenceNumber: values.bpayReferenceNumber ?? null,
      notes: values.notes ?? '',
      direction: values.direction ?? TaskDirection.SENDING,
      sendSMS: values.sendSMS,
      // sendEmail: values.sendEmail,
      dueAt,
      ...(isTax ? taxInfo : {}),
      ...(payable ? payableInfo : {}),
      ...(signable ? signableInfo : {}),
      paymentTypes: payable ? payableInfo.paymentTypes : [],
      documents: signableInfo.documents,
      //@ts-ignore
      supportDocuments: values.supportDocuments?.map(
        ({ identityId, key, level, type, name }) => ({
          identityId,
          key,
          level,
          type,
          name,
        })
      ),
      invoiceStatus: values.invoiceStatus ?? InvoiceStatus.INVOICE,
      emailMessage: values.emailMessage ?? '',
      sendTemplate: values.sendTemplate ?? false,
      templateJson: values.sendTemplate ? values.templateJson : null,
      templateHtml: values.sendTemplate ? values.templateHtml : null,
      documentAnalysisId: values.documentAnalysisId ?? undefined,
      period: values.period ?? undefined,
      year: values.year ?? undefined,
    };

    if (Array.isArray(values.signers)) {
      const signerInputs = values.signers
        .filter((signer) => signer?.id && signer.signerType)
        .map((signer) => ({
          userId: signer.id,
          signerType: signer.signerType,
        }));
      if (signerInputs.length > 0) {
        task.signers = signerInputs as SignerInput[];
      }
    }
    if (
      !(
        options.taskType === 'invoice' ||
        (options.taskType === 'esignature' &&
          task.type === 'SIGN_PAY' &&
          task.direction === 'SENDING')
      )
    ) {
      task.invoiceStatus = null;
    }

    if (values.bindingId) {
      task.bindingId = values.bindingId;
    }
    if (task.invoiceStatus === InvoiceStatus.QUOTE) {
      task.type = TaskType.QUOTE;
      // TODO: uncommenet below after schema is updated
      if (values.automaticGenerateInvoice) {
        task.automaticGenerateInvoice = values.automaticGenerateInvoice;
        task.automaticInvoicePaymentTerms = values.automaticInvoicePaymentTerms;
      }
      if (values.quoteRequiresSignature) {
        task.quoteRequiresSignature = values.quoteRequiresSignature;
      }
    }
    if (values.paymentFrequency !== PaymentFrequency.ONCE) {
      task.numberOfPayments = values.numberOfPayments;
    } else {
      task.numberOfPayments = 1;
    }

    if (options.status) {
      task.status = options.status as any;
    } else if (options.savingAsDraft) {
      task.status = CreateTaskStatus.DRAFT;
      // if (!task.amount) task.amount = 1;
    }
    if (
      values.futureTaskInput &&
      Object.keys(values.futureTaskInput).length > 0
    ) {
      task.futureLinkedTask = {
        fromId: values.futureTaskInput.from?.id,
        fromType:
          isEntityOrContact(values.futureTaskInput.from) ?? FromToType.CONTACT,
        toId: values.futureTaskInput.to?.id,
        toType:
          isEntityOrContact(values.futureTaskInput.to) ?? FromToType.CONTACT,
        lineItems: values.futureTaskInput.lineItems.map(
          transformDocumentAnalysisLineItemInput
        ),
        bpayReferenceNumber:
          values.settlementStatus === TaskSettlementStatus.PAYABLE
            ? values.futureTaskInput.bpayReferenceNumber
            : undefined,
        numberOfPayments:
          values.futureTaskInput.paymentFrequency !== PaymentFrequency.ONCE
            ? values.futureTaskInput.numberOfPayments
            : 1,
        paymentFrequency: values.futureTaskInput.paymentFrequency,
        isUnlimitedRecurring:
          values.futureTaskInput.paymentFrequency &&
          values.futureTaskInput.paymentFrequency !== PaymentFrequency.ONCE,
        reference: values.futureTaskInput.reference || values.reference,
        amount: values.futureTaskInput.amount,
        dueAt: values.futureTaskInput.dueAt,
      };
    }
    if (
      task.futureLinkedTask &&
      task.futureLinkedTask?.reference !== task.reference
    ) {
      task.futureLinkedTask.reference = task.reference ?? '';
    }

    if (options.taskType === 'esignature' && task.type === 'SIGN_PAY') {
      if (task.futureLinkedTask && !task.futureLinkedTask?.fromId) {
        task.futureLinkedTask.fromId = task.fromId;
        task.futureLinkedTask.fromType = task.fromType;
      }
    }

    // If guest todo, create contact first in futureLinkedTask
    if (
      task.futureLinkedTask &&
      values.futureTaskInput?.to?.signerType === 'GUEST'
    ) {
      const createdContact = await createContact({
        variables: {
          input: {
            entityId: currentEntityId,
            firstName: values.futureTaskInput.to.firstName ?? '',
            lastName: values.futureTaskInput.to.lastName ?? '',
            email: values.futureTaskInput.to.email,
            phone: values.futureTaskInput.to.phone,
            name: values.futureTaskInput.to.name,
            taxNumber: values.futureTaskInput.to.taxNumber,
          },
        },
      });
      task.futureLinkedTask.toId = createdContact.data.createContact.id;
      task.futureLinkedTask.toType = FromToType.CONTACT;
    }

    if (
      task.futureLinkedTask &&
      task.settlementStatus === TaskSettlementStatus.NIL
    ) {
      task.futureLinkedTask.amount = 0;
      task.futureLinkedTask.lineItems = [];
    }

    if (id && entityId) {
      if (options.status) {
        task.status = options.status as any;
      } else if (!options.savingAsDraft) {
        task.status = UpdateTaskStatus.INCOMPLETE as any;
      }
      return {
        id,
        entityId,
        ...task,
      };
    }
    return task;
  };

  const preESignatureSubmit = (values: BillCreateFormData) => {
    values = _.cloneDeep(values);
    values.type = TaskType.SIGN_ONLY;
    // if (values.direction === TaskDirection.SENDING) values.to = null;
    if (
      values.futureTaskInput &&
      Object.keys(values.futureTaskInput).length > 0
    ) {
      values.type = TaskType.SIGN_PAY;
    } else values.futureTaskInput = undefined;
    return values;
  };

  return createTaskInputValues;
}
